/**
 * Self-test suite for SecureValueRecovery (SVR PIN-protected backup).
 *
 * Mirrors iOS SVRSelfTest.swift case-for-case and adds web-only cases
 * (header tamper via AAD, algorithm discriminator, empty blob).
 *
 * Hit the pure-crypto path only — no SignalStore writes, no network — so it
 * is safe to invoke from any context including headless vitest runs.
 *
 * Registered with the global SelfTestRunner singleton at module load time.
 * The runner auto-discovers this file via Vite `import.meta.glob` in
 * `selftest/index.ts` (matches `**\/*SelfTest.ts`).
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  seal,
  open,
  rewrap,
  normalizePin,
  PolicyError,
  WrongPinError,
  TamperedError,
  UnsupportedAlgorithmError,
  SVR_BLOB_VERSION,
  type SVRBackupBlob,
  type SVRKDFParams,
  type SealedBundle,
} from "./SecureValueRecovery";

// ---------------------------------------------------------------------------
// Fixtures
// ---------------------------------------------------------------------------

/**
 * PBKDF2 with production iteration count (600k) would make the suite take
 * ~30 s in the browser. The crypto envelope is identical with a low count;
 * we are testing *behaviour* (correct errors, round-trips) not parameter
 * strength.
 */
const FAST_PARAMS: SVRKDFParams = {
  algorithm: "PBKDF2-SHA256",
  iterations: 1_000,
  keyBytes: 32,
  saltBytes: 16,
};

function sampleBlob(): SVRBackupBlob {
  return {
    version: SVR_BLOB_VERSION,
    identityPublicKey: new Uint8Array(32).fill(0xa1),
    identityPrivateKey: new Uint8Array(32).fill(0xb2),
    registrationId: 4242,
    deviceId: 1,
    trustedIdentities: {
      alice: new Uint8Array(32).fill(0x01),
      bob: new Uint8Array(32).fill(0x02),
    },
  };
}

/** Compare two SVRBackupBlob instances field-by-field. */
function blobsEqual(a: SVRBackupBlob, b: SVRBackupBlob): boolean {
  if (
    a.version !== b.version ||
    a.registrationId !== b.registrationId ||
    a.deviceId !== b.deviceId
  ) {
    return false;
  }
  if (!arraysEqual(a.identityPublicKey, b.identityPublicKey)) return false;
  if (!arraysEqual(a.identityPrivateKey, b.identityPrivateKey)) return false;
  const aKeys = Object.keys(a.trustedIdentities).sort();
  const bKeys = Object.keys(b.trustedIdentities).sort();
  if (JSON.stringify(aKeys) !== JSON.stringify(bKeys)) return false;
  for (const k of aKeys) {
    if (!arraysEqual(a.trustedIdentities[k]!, b.trustedIdentities[k]!)) return false;
  }
  return true;
}

function arraysEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Individual test cases
// ---------------------------------------------------------------------------

/** 1. Round-trip: seal then open with the correct PIN recovers the blob exactly. */
async function testRoundTrip(): Promise<TestCase> {
  const name = "round-trip with correct PIN";
  try {
    const blob = sampleBlob();
    const bundle = await seal(blob, "123456", FAST_PARAMS);
    const recovered = await open(bundle, "123456");
    if (!blobsEqual(blob, recovered)) {
      return fail(name, "blob mismatch after open()");
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

/** 2. Wrong PIN → WrongPinError (not a crash, not silent garbage). */
async function testWrongPinRejected(): Promise<TestCase> {
  const name = "wrong PIN is rejected";
  try {
    const bundle = await seal(sampleBlob(), "correct-horse", FAST_PARAMS);
    try {
      await open(bundle, "wrong-horse");
      return fail(name, "open() succeeded with wrong PIN");
    } catch (e) {
      if (e instanceof WrongPinError) return pass(name);
      return fail(name, `expected WrongPinError, got ${e}`);
    }
  } catch (e) {
    return fail(name, `seal() failed: ${e}`);
  }
}

/** 3. PIN change (rewrap): new PIN opens; old PIN throws. */
async function testPinChange(): Promise<TestCase> {
  const name = "PIN change preserves blob";
  try {
    const blob = sampleBlob();
    const bundle = await seal(blob, "old-pin-1", FAST_PARAMS);
    const rewrapped = await rewrap(bundle, "old-pin-1", "new-pin-2", FAST_PARAMS);

    const recovered = await open(rewrapped, "new-pin-2");
    if (!blobsEqual(blob, recovered)) {
      return fail(name, "blob mismatch via new PIN");
    }

    try {
      await open(rewrapped, "old-pin-1");
      return fail(name, "old PIN still opens bundle after rewrap");
    } catch (e) {
      if (!(e instanceof WrongPinError)) {
        return fail(name, `expected WrongPinError for old PIN, got ${e}`);
      }
    }

    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

/**
 * 4. Tampered ciphertext: flip a byte in encBlob bytes → TamperedError.
 * Mirrors iOS testTamperedBlobRejected which corrupts bundle.encBlob.
 */
async function testTamperedCiphertext(): Promise<TestCase> {
  const name = "tampered ciphertext is rejected";
  try {
    const bundle = await seal(sampleBlob(), "pin12345", FAST_PARAMS);

    // Decode encBlob, flip middle byte, re-encode.
    const encBlobBytes = fromBase64url(bundle.encBlob);
    encBlobBytes[Math.floor(encBlobBytes.length / 2)] ^= 0x01;

    const tampered: SealedBundle = {
      ...bundle,
      encBlob: toBase64url(encBlobBytes),
    };

    try {
      await open(tampered, "pin12345");
      return fail(name, "open() succeeded on corrupted encBlob");
    } catch (e) {
      if (e instanceof TamperedError) return pass(name);
      return fail(name, `expected TamperedError, got ${e}`);
    }
  } catch (e) {
    return fail(name, `seal() failed: ${e}`);
  }
}

/** 5. Short PIN → PolicyError before any crypto runs. */
async function testShortPinRejected(): Promise<TestCase> {
  const name = "short PIN is rejected";
  try {
    await seal(sampleBlob(), "12", FAST_PARAMS);
    return fail(name, "seal() accepted PIN below minimum length");
  } catch (e) {
    if (e instanceof PolicyError) return pass(name);
    return fail(name, `expected PolicyError, got ${e}`);
  }
}

/**
 * 6. Non-determinism: two seal() calls with the same PIN + blob produce
 * different ciphertexts (random salt + nonce per call).
 */
async function testNonDeterminism(): Promise<TestCase> {
  const name = "same PIN produces different ciphertexts each backup";
  try {
    const blob = sampleBlob();
    const b1 = await seal(blob, "same-pin", FAST_PARAMS);
    const b2 = await seal(blob, "same-pin", FAST_PARAMS);
    if (b1.salt === b2.salt) {
      return fail(name, "salt was identical across two seal() calls");
    }
    if (b1.encMasterKey === b2.encMasterKey) {
      return fail(name, "encMasterKey was identical (same nonce reuse)");
    }
    if (b1.encBlob === b2.encBlob) {
      return fail(name, "encBlob was identical (same nonce reuse)");
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

/**
 * 7. Header tamper: flip a byte in the iterations field of the KDF params
 * (which is part of the AAD). The GCM tag on encMasterKey must fail.
 */
async function testHeaderTamper(): Promise<TestCase> {
  const name = "tampered header (iterations) invalidates GCM tag";
  try {
    const bundle = await seal(sampleBlob(), "test-pin", FAST_PARAMS);

    // Claim a different iteration count — same ciphertext, different AAD.
    const tampered: SealedBundle = {
      ...bundle,
      kdf: { ...bundle.kdf, iterations: bundle.kdf.iterations + 1 },
    };

    try {
      await open(tampered, "test-pin");
      return fail(name, "open() succeeded with tampered iterations count");
    } catch (e) {
      if (e instanceof WrongPinError || e instanceof TamperedError) {
        // Both are valid: wrong AAD → GCM failure on encMasterKey → WrongPinError,
        // or in theory on encBlob → TamperedError.
        return pass(name);
      }
      return fail(name, `expected WrongPinError or TamperedError, got ${e}`);
    }
  } catch (e) {
    return fail(name, `seal() failed: ${e}`);
  }
}

/**
 * 8. Unknown KDF discriminator → UnsupportedAlgorithmError, not a crash.
 */
async function testUnknownAlgorithm(): Promise<TestCase> {
  const name = "unknown KDF discriminator throws UnsupportedAlgorithmError";
  try {
    const bundle = await seal(sampleBlob(), "test-pin", FAST_PARAMS);

    // Forge a bundle that claims to use a future algorithm.
    const tampered: SealedBundle = {
      ...bundle,
      kdf: { ...bundle.kdf, algorithm: "Argon2id" as never },
    };

    try {
      await open(tampered, "test-pin");
      return fail(name, "open() succeeded with unsupported algorithm");
    } catch (e) {
      if (e instanceof UnsupportedAlgorithmError) return pass(name);
      return fail(name, `expected UnsupportedAlgorithmError, got ${e}`);
    }
  } catch (e) {
    return fail(name, `seal() failed: ${e}`);
  }
}

/** 9. Empty blob: sealing an empty trustedIdentities + zero-length keys works. */
async function testEmptyBlob(): Promise<TestCase> {
  const name = "empty blob seals and opens correctly";
  try {
    const empty: SVRBackupBlob = {
      version: SVR_BLOB_VERSION,
      identityPublicKey: new Uint8Array(0),
      identityPrivateKey: new Uint8Array(0),
      registrationId: 0,
      deviceId: 1,
      trustedIdentities: {},
    };
    const bundle = await seal(empty, "abcd", FAST_PARAMS);
    const recovered = await open(bundle, "abcd");
    if (!blobsEqual(empty, recovered)) {
      return fail(name, "empty blob mismatch after open()");
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

/** 10. PIN normalisation: whitespace trim means "  hunter2 " === "hunter2". */
async function testPinNormalisation(): Promise<TestCase> {
  const name = "PIN normalisation (whitespace trim)";
  try {
    const blob = sampleBlob();
    const bundle = await seal(blob, "  hunter2 ", FAST_PARAMS);
    const recovered = await open(bundle, "hunter2");
    if (!blobsEqual(blob, recovered)) {
      return fail(name, "blob mismatch — whitespace trim not symmetric");
    }
    return pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// Helpers (local, not exported — duplicate of what SecureValueRecovery uses
// internally, kept here so the suite is self-contained and doesn't import
// private symbols)
// ---------------------------------------------------------------------------

function toBase64url(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

function fromBase64url(s: string): Uint8Array {
  const padded = s.replace(/-/g, "+").replace(/_/g, "/");
  const padLen = (4 - (padded.length % 4)) % 4;
  const b64 = padded + "=".repeat(padLen);
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "SVR (PIN-protected backup)",
  isVerified: true,
  run: async () =>
    buildSuiteResult(
      await Promise.all([
        testRoundTrip(),
        testWrongPinRejected(),
        testPinChange(),
        testTamperedCiphertext(),
        testShortPinRejected(),
        testNonDeterminism(),
        testHeaderTamper(),
        testUnknownAlgorithm(),
        testEmptyBlob(),
        testPinNormalisation(),
      ]),
    ),
});
